import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a product drawer that loads content via section rendering.
 *
 * @extends {DialogComponent}
 */
class ProductDrawerComponent extends DialogComponent {
  /**
   * The section ID for the product drawer
   */
  static SECTION_ID = 'product-drawer';

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for product drawer open events
    document.addEventListener('product-drawer:open', this.#handleProductDrawerOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('product-drawer:open', this.#handleProductDrawerOpen);
  }

  /**
   * Handles the product drawer open event
   * @param {CustomEvent} event - The custom event containing product details
   */
  #handleProductDrawerOpen = async (event) => {
    const { productHandle, productId } = event.detail;
    
    if (!productHandle && !productId) {
      console.error('Product handle or ID is required to open the product drawer');
      return;
    }

    await this.openWithProduct(productHandle, productId);
  };

  /**
   * Opens the drawer with a specific product
   * @param {string} productHandle - The product handle
   * @param {string|number} productId - The product ID
   */
  async openWithProduct(productHandle, productId) {
    // Show the drawer first
    this.showDialog();
    
    // Show loading state
    const contentContainer = this.refs.dialog.querySelector('.product-drawer__content');
    if (contentContainer) {
      contentContainer.innerHTML = '<div class="product-drawer__loading">Loading...</div>';
    }

    try {
      // Build the product URL
      const productUrl = `/products/${productHandle}`;
      
      console.log('Loading product drawer for:', productHandle);
      
      // Build URL with section_id to get only the product-drawer section
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', ProductDrawerComponent.SECTION_ID);
      
      // Fetch the section HTML
      const response = await fetch(url.toString());
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const sectionHTML = await response.text();
      console.log('Section HTML received, length:', sectionHTML?.length);

      // Parse the HTML and extract the content
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const sectionContent = doc.querySelector('.product-drawer-content');

      console.log('Section content found:', !!sectionContent);

      if (sectionContent && contentContainer) {
        contentContainer.innerHTML = sectionContent.outerHTML;
        
        // Initialize the product form
        this.#initializeProductForm();
      } else {
        console.error('Section content not found in response');
        if (contentContainer) {
          contentContainer.innerHTML = '<div class="product-drawer__error">Failed to load product details. Product may not exist.</div>';
        }
      }
    } catch (error) {
      console.error('Error loading product drawer:', error);
      const contentContainer = this.refs.dialog.querySelector('.product-drawer__content');
      if (contentContainer) {
        contentContainer.innerHTML = `<div class="product-drawer__error">Failed to load product details: ${error.message}</div>`;
      }
    }
  }

  /**
   * Initializes the product form to handle add to cart
   */
  #initializeProductForm() {
    const form = /** @type {HTMLFormElement} */ (this.refs.dialog.querySelector('.product-drawer-form'));
    if (!form) return;

    // Initialize variant selection
    this.#initializeVariantSelection(form);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      
      const formData = new FormData(form);
      const submitButton = /** @type {HTMLButtonElement} */ (form.querySelector('button[type="submit"]'));
      
      // Disable button and show loading state
      if (submitButton) {
        submitButton.disabled = true;
        const originalText = submitButton.textContent;
        submitButton.textContent = 'Adding...';
        
        try {
          // Get cart drawer section IDs for updating
          const cartItemsComponents = document.querySelectorAll('cart-items-component');
          const sectionsToUpdate = new Set();
          cartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement && item.dataset.sectionId) {
              sectionsToUpdate.add(item.dataset.sectionId);
            }
          });

          // Add sections parameter to the form data to get updated cart sections
          formData.append('sections', Array.from(sectionsToUpdate).join(','));
          formData.append('sections_url', window.location.pathname);

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            const data = await response.json();
            
            // Dispatch cart update event (note: CartAddEvent.eventName is 'cart:update')
            document.dispatchEvent(new CartAddEvent(
              data,
              'product-drawer',
              {
                source: 'product-drawer',
                itemCount: data.items?.length || 0,
                variantId: data.variant_id || data.id,
                sections: data.sections || {}
              }
            ));

            // Close the drawer
            this.closeDialog();
          } else {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          alert(error.message || 'Failed to add product to cart. Please try again.');
        } finally {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalText;
          }
        }
      }
    });
  }

  /**
   * Initializes variant selection handling
   * @param {HTMLFormElement} form - The product form
   */
  #initializeVariantSelection(form) {
    const variantRadios = form.querySelectorAll('input[type="radio"][name^="option"]');
    const variantIdInput = /** @type {HTMLInputElement} */ (form.querySelector('[data-variant-id]'));
    const submitButton = /** @type {HTMLButtonElement} */ (form.querySelector('button[type="submit"]'));
    const variantsDataElement = form.querySelector('[data-product-variants]');
    const variantPricesDataElement = form.querySelector('[data-product-variant-prices]');
    const priceContainer = form
      .closest('.product-drawer-content')
      ?.querySelector('.product-drawer__price [ref="priceContainer"]');

    if (!variantsDataElement || variantRadios.length === 0) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsDataElement.textContent);
    } catch (e) {
      console.error('Failed to parse product variants:', e);
      return;
    }

    /** @type {Map<number, string>} */
    const variantPriceHtmlMap = new Map();
    if (variantPricesDataElement?.textContent) {
      try {
        const parsedVariantPrices = JSON.parse(variantPricesDataElement.textContent);
        parsedVariantPrices.forEach((variantPrice) => {
          if (variantPrice?.id && typeof variantPrice.price_html === 'string') {
            variantPriceHtmlMap.set(Number(variantPrice.id), variantPrice.price_html);
          }
        });
      } catch (e) {
        console.error('Failed to parse product variant prices:', e);
      }
    }

    /**
     * Updates drawer price markup for the selected variant.
     * @param {{ id: number|string }} variant
     */
    const updatePriceDisplay = (variant) => {
      if (!(priceContainer instanceof HTMLElement)) return;
      const priceHtml = variantPriceHtmlMap.get(Number(variant.id));
      if (!priceHtml) return;
      priceContainer.innerHTML = priceHtml;
    };

    const getSelectedOptions = () => {
      /** @type {string[]} */
      const selectedOptions = [];
      const fieldsets = form.querySelectorAll('fieldset[data-option-position]');

      fieldsets.forEach((fieldsetEl) => {
        const fieldset = /** @type {HTMLElement} */ (fieldsetEl);
        const position = parseInt(fieldset.dataset.optionPosition || '1');
        const checked = fieldset.querySelector('input[type="radio"]:checked');

        if (checked instanceof HTMLInputElement) {
          selectedOptions[position - 1] = checked.value;
        }
      });

      return selectedOptions;
    };

    const updateAvailability = () => {
      const selectedOptions = getSelectedOptions();
      
      // Update each option's availability based on current selection
      const fieldsets = form.querySelectorAll('fieldset[data-option-position]');
      
      fieldsets.forEach(/** @param {Element} fieldset */ (fieldset) => {
        const position = parseInt(/** @type {HTMLElement} */ (fieldset).dataset.optionPosition || '1');
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        
        radios.forEach(/** @param {Element} radioEl */ (radioEl) => {
          const radio = /** @type {HTMLInputElement} */ (radioEl);
          const value = radio.value;
          
          // Build theoretical options array with this value
          const testOptions = [...selectedOptions];
          testOptions[position - 1] = value;
          
          // Check if any variant matches these options and is available
          const isAvailable = variants.some(/** @param {any} variant */ (variant) => {
            return variant.options.every(
              /** @param {any} option @param {number} index */ (option, index) => {
                if (index === position - 1) return option === value;
                if (testOptions[index]) return option === testOptions[index];
                return true; // If no option selected yet, don't filter it out
              }
            ) && variant.available;
          });
          
          const label = /** @type {HTMLElement} */ (radio.nextElementSibling);
          if (label) {
            if (isAvailable) {
              radio.classList.remove('disabled');
              label.classList.remove('disabled');
            } else {
              radio.classList.add('disabled');
              label.classList.add('disabled');
            }
          }
        });
      });
    };

    const updateVariant = () => {
      updateAvailability();
      
      const selectedOptions = getSelectedOptions();
      
      // Find matching variant
      const matchingVariant = variants.find(/** @param {any} variant */ (variant) => {
        return variant.options.every(
          /** @param {any} option @param {number} index */ (option, index) => option === selectedOptions[index]
        );
      });

      if (submitButton) {
        if (matchingVariant && variantIdInput) {
          // Update variant ID
          variantIdInput.value = matchingVariant.id;
          updatePriceDisplay(matchingVariant);

          // Update button state
          if (matchingVariant.available) {
            submitButton.disabled = false;
            submitButton.textContent = 'Add to Cart';
          } else {
            submitButton.disabled = true;
            submitButton.textContent = 'Sold Out';
          }
        } else {
          // No variant exists for this combination; disable add to cart
          submitButton.disabled = true;
          submitButton.textContent = 'Unavailable';
        }
      }
    };

    // Add change listeners to all variant radio buttons
    variantRadios.forEach(radio => {
      radio.addEventListener('change', updateVariant);
    });
    
    // Initial availability check
    updateAvailability();
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('product-drawer-component')) {
  customElements.define('product-drawer-component', ProductDrawerComponent);
}

/**
 * Helper function to open the product drawer
 * @param {string} productHandle - The product handle
 * @param {string|number} productId - The product ID
 */
export function openProductDrawer(productHandle, productId) {
  document.dispatchEvent(new CustomEvent('product-drawer:open', {
    bubbles: true,
    detail: { productHandle, productId }
  }));
}

